import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { BaseWidgetOptions, WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { NoData } from '@texas/components/shared/NoData';
import { shipmentsApi } from '@texas/api/endpoints/shipmentsApi';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { Flex } from '@chakra-ui/react';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { shipmentEvents } from '@texas/components/shared/shipment/events';
import { transportRequestEvents } from '@texas/components/shared/transportRequests/events';
import { ShipmentDetailsModal } from '@texas/components/shared/shipment/ShipmentDetailsModal';
import { ShipmentItem } from '@texas/components/shared/shipment/ShipmentItem';

interface Props extends BaseWidgetProps {
  options?: BaseWidgetOptions;
}

export function MyShipmentsWidget({
  color,
  customTitle,
  options,
  widgetId,
}: Props) {
  const { t } = useTranslation();

  const { data, refetch, loading } = useApiResource(
    shipmentsApi.myActiveShipments,
  );
  const {
    value: detailsValue,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
    isOpen: isDetailsOpen,
  } = useValueDisclosure<number>();

  useEffect(() => {
    refetch();
  }, [refetch]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch();
    },
    signalEvents: [
      shipmentEvents.onShipmentCreated,
      transportRequestEvents.onSamplesAdded,
      shipmentEvents.onShipmentDispatched,
      shipmentEvents.onShipmentUnpacked,
    ],
  });

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      settings={options?.componentSettings}
      customColor={color}
      type={WidgetType.MyActiveShipments}
      style="default"
      header={customTitle ?? t('shipment.active')}
    >
      {(data ?? []).length === 0 ? (
        <NoData label={t('shipment.noneActive')} showIcon={true} />
      ) : (
        <Flex gap={2} flexDir="column">
          {data?.map((x) => {
            return (
              <ShipmentItem
                key={x.id}
                shipment={x}
                onClick={() => onDetailsOpen(x.id)}
              />
            );
          })}
        </Flex>
      )}
      {detailsValue && (
        <ShipmentDetailsModal
          shipmentId={detailsValue}
          isOpen={isDetailsOpen}
          onClose={onDetailsClose}
        />
      )}
    </WidgetWrapper>
  );
}
